import React, { useEffect, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Plus, Trash2, Send, Loader2, FileText, ExternalLink, Copy, Printer, RefreshCw, Ban } from "lucide-react";
import { fmtMoney, toCents, MIN_CENTS } from "./money";
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { emailField, requiredField } from "../lib/validation";
import useTenantCurrency from "../lib/useTenantCurrency";
import { isLegacyCurrency } from "./CatalogueCurrencyField";

// QA2 H2-12 — this was the ONLY money field in the dashboard that asked a human for
// cents (`AMOUNT (IN CENTS)`, bare type=number, no min/step). A practitioner typing the
// amount they meant to bill — `120.50` — got `parseInt("120.50")` = 120 cents, i.e. a
// CA$1.20 invoice, sent with no review step. Same conversion as `admin/money.js`'s
// `toCents`, which is the ONE money-entry pattern in frontend/src/admin: the form speaks
// the currency's major unit, the wire speaks cents, and Math.round is what closes the
// float gap (120.50 * 100 = 12049.999… in IEEE-754).

// Stripe rejects a charge under 50 minor units for every currency this product sells in
// (CAD/USD/EUR). Refusing here means the practitioner learns it while the form is still
// on screen, instead of at `send` time as a 500 (H2-13).

// NO `currency`: the form never offered one, and the hardcoded "cad" it sent denominated
// every invoice in a currency the platform may not settle in — which the money core
// refuses. The server denominates it, and the form STATES what it will be.
const emptyForm = { client_name: "", client_email: "", amount: "100.00", description: "" };

// The description names the Stripe line item, which Stripe refuses past a fixed length on
// every attempt; the server refuses a longer one with a 422 (`server.InvoiceIn`). Same value
// as `modules/payments/schemas.INVOICE_DESCRIPTION_MAX_CHARS` (pinned by
// tests/test_d5_invoice_numbers_live.py). zod counts UTF-16 units and the server code
// points, so the form is never laxer than the server.
const INVOICE_DESCRIPTION_MAX_CHARS = 1000;

// server.py's InvoiceIn requires client_name, client_email (EmailStr — a real, validated
// address, since it is where the payment link is sent) and amount_cents (gt=0). The
// amount check stays a superRefine over the raw string, same shape as
// ServicesAdmin.jsx's price check: MIN_CENTS is Stripe's own floor, not a generic rule.
const INVOICE_SCHEMA = z.object({
  client_name: requiredField(200),
  client_email: emailField,
  amount: z.string(),
  description: z.string().trim().max(INVOICE_DESCRIPTION_MAX_CHARS, "validation.tooLong"),
}).superRefine((val, ctx) => {
  const cents = toCents(val.amount);
  if (!Number.isFinite(cents) || cents < MIN_CENTS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["amount"], message: "invoices.amountTooSmall" });
  }
});

// The refusals the invoice routes answer as `{detail: {code, message}}` (contract §2.3-2.5).
// The CODE is translated; the `message` is an English developer diagnostic and never
// reaches the practitioner. Any other refusal keeps the generic `extractApiError` path.
const INVOICE_ERROR_KEYS = {
  INVOICE_NOT_DELETABLE: "invoices.error.notDeletable",
  INVOICE_NOT_CANCELLABLE: "invoices.error.notCancellable",
  INVOICE_NOT_SENDABLE: "invoices.error.notSendable",
  INVOICE_NUMBER_BUSY: "invoices.error.numberBusy",
  // A concurrent send of the same invoice (double-click, two tabs) recorded its link first;
  // this one's session was expired and nothing was mailed (`set_invoice_sent` CAS).
  INVOICE_SENT_CONCURRENTLY: "invoices.error.sentConcurrently",
  // Refused BEFORE the invoice is numbered, because Stripe would refuse it on every attempt
  // (`payments/service._refuse_unsendable_invoice`): the draft stays unnumbered and can
  // still be deleted, which is what the under-minimum sentence tells the practitioner to do.
  INVOICE_AMOUNT_BELOW_MINIMUM: "invoices.error.belowMinimum",
  PAYMENTS_UNAVAILABLE: "invoices.error.paymentsUnavailable",
  // Only a legacy or hand-built row: the form and `InvoiceIn` both bound a new description.
  INVOICE_DESCRIPTION_TOO_LONG: "invoices.error.descriptionTooLong",
};

// The interpolation variables a code's sentence takes. The minimum is the server's
// (`min_cents` in the refusal), falling back to the form's own floor only if it is absent.
const INVOICE_ERROR_VARS = {
  INVOICE_AMOUNT_BELOW_MINIMUM: (detail, currency) => ({
    min: ((Number.isFinite(detail.min_cents) ? detail.min_cents : MIN_CENTS) / 100).toFixed(2),
    currency,
  }),
  INVOICE_DESCRIPTION_TOO_LONG: (detail) => ({
    max: Number.isFinite(detail.max_chars) ? detail.max_chars : INVOICE_DESCRIPTION_MAX_CHARS,
  }),
};

/** The sentence for an invoice-route refusal, in the practitioner's language. `currency`
 *  is the invoice's own (upper-case code), for the sentences that name an amount. */
function invoiceErrorMessage(e, t, fallback, currency = "") {
  const detail = e?.response?.data?.detail;
  const code = detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code : undefined;
  const key = INVOICE_ERROR_KEYS[code];
  return key ? t(key, INVOICE_ERROR_VARS[code]?.(detail, currency)) : extractApiError(e, fallback);
}

// An invoice can be cancelled until it is paid; a numbered one keeps its number for ever.
const CANCELLABLE_STATUSES = new Set(["draft", "sent", "overdue_notified"]);

// The payment link is offered only while the client can still be asked to pay with it. A
// cancelled invoice's session is expired best-effort only (a failed expiry is logged), so
// its link may still be payable and must not be handed out; a paid or refunded one has
// nothing left to collect.
const PAYABLE_LINK_STATUSES = new Set(["sent", "overdue_notified"]);

export default function InvoicesAdmin() {
  const t = useT("crud");
  // The catalogue.currency.* keys live in the "admin" namespace — the same source
  // ProgramsAdmin.jsx/ProductsAdmin.jsx/ServicesAdmin.jsx already read for this exact
  // mechanism (BUG-207/BUG-212).
  const ta = useT("admin");
  const tenantCurrency = useTenantCurrency();
  // "" while the platform currency is still being read — the labels then omit it rather
  // than naming a currency this screen only guessed.
  const currencyLabel = (tenantCurrency || "").toUpperCase();
  const tc = useT("common");
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);
  const [resending, setResending] = useState(null);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const updateField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };

  const load = async () => {
    const { data } = await api.get("/admin/invoices");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const amountCents = toCents(form.amount);
  const amountValid = Number.isFinite(amountCents) && amountCents >= MIN_CENTS;

  // BUG-053 — WHAT THE CLIENT WILL ACTUALLY BE CHARGED. With tax lines configured this is
  // not the amount typed: a 100.00 invoice under GST 5% + QST 9.975% bills 114.98, and the
  // practitioner used to be told "This will bill 100.00 USD" while their client was asked
  // for 114.98.
  //
  // THE TAX IS NOT COMPUTED HERE. `platform_helpers.compute_tax_split` is the one tax
  // engine; a second copy of it in JavaScript is exactly the duplication that lets a
  // preview and a charge drift apart. The screen asks the server instead —
  // GET /tenant/checkout-breakdown exists for this, and its own docstring says a preview
  // and the charge it previews cannot disagree — and reports the answer.
  //
  // `null` means "not known": either still in flight, or the read failed. The echo then
  // says the pre-tax amount AND says that it is pre-tax, rather than presenting it as the
  // total (the defect) or going blank.
  const [breakdown, setBreakdown] = useState(null);
  useEffect(() => {
    if (!amountValid) { setBreakdown(null); return undefined; }
    let alive = true;
    // Debounced: the amount field fires per keystroke, and a quote per keystroke is a
    // request storm for a number the practitioner is still typing.
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/tenant/checkout-breakdown?service_type=invoice&amount_cents=${amountCents}`);
        if (alive) setBreakdown(data || null);
      } catch {
        if (alive) setBreakdown(null);
      }
    }, 300);
    return () => { alive = false; clearTimeout(id); };
  }, [amountCents, amountValid]);

  // The named tax lines, in the server's own shape (`name` / `amount_cents` — the keys
  // `split_amount_over_tax_lines` emits), so "GST 5.00 USD and QST 9.98 USD" rather than
  // one merged figure a client cannot reconcile.
  const taxLines = (breakdown?.tax_breakdown || [])
    .filter((l) => Number(l.amount_cents) > 0)
    .map((l) => `${l.name} ${fmtMoney(l.amount_cents, tenantCurrency)}`);

  const create = async () => {
    if (validate(INVOICE_SCHEMA, form)) { toast.error(tc("validation.fixHighlighted")); return; }
    setBusy(true);
    try {
      const { client_name, client_email, description } = form;
      await api.post("/admin/invoices", {
        client_name, client_email, description,
        amount_cents: amountCents,
        locale: lang,
      });
      setForm(emptyForm);
      await load();
      toast.success(t("invoices.created", { amount: fmtMoney(amountCents, tenantCurrency) },
        "Invoice created for {amount}."));
    } catch (e) {
      toast.error(extractApiError(e, tc("errors.generic", null, "Something went wrong.")));
    } finally { setBusy(false); }
  };

  const send = async (inv) => {
    setSending(inv.id);
    try {
      await api.post(`/admin/invoices/${inv.id}/send`);
      await load();
      toast.success(t("invoices.sent", { email: inv.client_email }, "Payment link sent to {email}."));
    } catch (e) {
      toast.error(invoiceErrorMessage(e, t, tc("errors.generic", null, "Something went wrong."),
        (inv.currency || tenantCurrency || "").toUpperCase()));
      // A refused send may still have changed the row (a concurrent send recorded its link,
      // a cancel or payment landed, Stripe failed after the number was taken): re-read it.
      // The refusal is the message; a failed re-read only leaves the previous list.
      await load().catch(() => {});
    } finally { setSending(null); }
  };

  const cancel = async (inv) => {
    // An unnumbered draft was never sent: it has no number to name or keep, and no payment
    // link to stop, so it is asked its own question.
    const question = inv.invoice_number
      ? t("invoices.confirmCancel", { number: inv.invoice_number })
      : t("invoices.confirmCancelDraft");
    if (!window.confirm(question)) return;
    try {
      await api.post(`/admin/invoices/${inv.id}/cancel`);
      await load();
      toast.success(t("invoices.cancelled"));
    } catch (e) {
      toast.error(invoiceErrorMessage(e, t, tc("errors.generic", null, "Something went wrong.")));
    }
  };

  const resendReceipt = async (inv) => {
    setResending(inv.id);
    try {
      await api.post(`/admin/receipts/${inv.receipt_number}/resend`);
      toast.success(t("invoices.receiptResent", { email: inv.client_email }));
    } catch (e) {
      toast.error(extractApiError(e, t("invoices.receiptResendError")));
    } finally { setResending(null); }
  };

  const remove = async (id) => {
    if (!window.confirm(tc("actions.confirmDelete", null, "Delete this invoice?"))) return;
    try {
      await api.delete(`/admin/invoices/${id}`);
      await load();
    } catch (e) {
      toast.error(invoiceErrorMessage(e, t, tc("errors.generic", null, "Something went wrong.")));
    }
  };

  return (
    <div data-testid="admin-invoices">
      <h1 className="font-display text-4xl">{t("invoices.title")}</h1>
      <p className="text-eh-muted mt-1">{t("invoices.subtitle")}</p>

      <div className="mt-8 bg-eh-cream border border-eh-border-soft rounded-3xl p-6">
        <h3 className="font-display text-xl mb-3">{t("invoices.new")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <Inp
            label={t("invoices.field.client_name")}
            value={form.client_name}
            testid="invoice-client-name"
            error={fieldErrors.client_name ? tc(fieldErrors.client_name) : ""}
            onChange={(v) => updateField("client_name", v)}
          />
          <Inp
            label={t("invoices.field.client_email")}
            value={form.client_email}
            type="email"
            testid="invoice-client-email"
            error={fieldErrors.client_email ? tc(fieldErrors.client_email) : ""}
            onChange={(v) => updateField("client_email", v)}
          />
          <Inp
            label={currencyLabel
              ? t("invoices.field.amountMajor", { currency: currencyLabel })
              : t("invoices.field.amount")}
            value={form.amount}
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            testid="invoice-amount"
            error={fieldErrors.amount
              ? t(fieldErrors.amount, { min: (MIN_CENTS / 100).toFixed(2), currency: currencyLabel },
                "Enter an amount of at least {min} {currency}.")
              : ""}
            onChange={(v) => updateField("amount", v)}
          />
          <Inp
            label={t("invoices.field.description")}
            value={form.description}
            testid="invoice-description"
            error={fieldErrors.description ? tc(fieldErrors.description) : ""}
            onChange={(v) => updateField("description", v)}
          />
          <button onClick={create} disabled={busy} className="btn-primary text-sm h-[40px]" data-testid="invoice-create">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} {tc("actions.create")}
          </button>
        </div>
        <div className="mt-3 text-xs" data-testid="invoice-amount-echo">
          {amountValid ? (
            <span className="text-eh-muted">
              {taxLines.length ? t(
                "invoices.willBillWithTax",
                {
                  amount: fmtMoney(breakdown.total_charged_cents, tenantCurrency),
                  subtotal: fmtMoney(breakdown.subtotal_cents, tenantCurrency),
                  taxes: taxLines.join(" + "),
                },
                "This will bill {amount} — {subtotal} plus {taxes}.",
              ) : breakdown ? t(
                "invoices.willBill",
                { amount: fmtMoney(breakdown.total_charged_cents, tenantCurrency) },
                "This will bill {amount}.",
              ) : t(
                "invoices.willBillBeforeTax",
                { amount: fmtMoney(amountCents, tenantCurrency) },
                "This will bill {amount} before tax.",
              )}
            </span>
          ) : (
            <span className="text-red-600">
              {t("invoices.amountTooSmall", { min: (MIN_CENTS / 100).toFixed(2), currency: currencyLabel },
                "Enter an amount of at least {min} {currency}.")}
            </span>
          )}
        </div>
      </div>

      <div className="mt-8 space-y-2">
        {items.length === 0 && <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-8 text-center">{t("invoices.empty")}</div>}
        {items.map((inv) => (
          <div key={inv.id} className="bg-eh-cream border border-eh-border-soft rounded-2xl p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-1"><div className="w-10 h-10 rounded-xl bg-eh-bg flex items-center justify-center text-eh-sage-deep"><FileText size={16} /></div></div>
            <div className="md:col-span-4">
              <div className="font-medium">
                {/* The number is what the client and the books call this invoice. Before
                    the first send there is none (a draft consumes no number), and a
                    cancelled invoice that was never sent never will have one. */}
                {inv.invoice_number
                  ? <span className="font-mono mr-2" data-testid={`invoice-number-${inv.id}`}>{inv.invoice_number}</span>
                  : <span className="text-eh-muted mr-2" data-testid={`invoice-number-${inv.id}`}>{inv.status === "draft" ? t("invoices.number.draft") : "—"}</span>}
                {inv.client_name}
              </div>
              <div className="text-xs text-eh-muted">
                {inv.client_email}
                {inv.receipt_number && (
                  <span className="font-mono text-eh-terracotta ml-1" data-testid={`invoice-receipt-tag-${inv.id}`}>
                    {t("invoices.receiptTag", { number: inv.receipt_number })}
                  </span>
                )}
              </div>
            </div>
            <div className="md:col-span-2 font-display text-lg">
              {fmtMoney(inv.amount_cents, inv.currency)}
              {/* BUG-207/BUG-212 — a legacy invoice held in a currency this practice no
                  longer prices in reaches `send_invoice_payment_link` and is refused
                  (400 CURRENCY_NOT_SETTLEABLE) with nothing said in advance. Same
                  mechanism ProgramsAdmin.jsx/ProductsAdmin.jsx/ServicesAdmin.jsx already
                  apply to their own rows — no new one invented here. */}
              {isLegacyCurrency(inv.currency, tenantCurrency) && (
                <div
                  className="mt-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 inline-block"
                  data-testid={`invoice-legacy-currency-${inv.id}`}
                >
                  {ta("catalogue.currency.notPayable", { held: (inv.currency || "").toUpperCase() })}
                </div>
              )}
            </div>
            <div className="md:col-span-2 text-sm text-eh-muted truncate">{inv.description || "—"}</div>
            <div className="md:col-span-1">
              <span data-testid={`invoice-status-${inv.id}`} className={`text-xs px-2 py-1 rounded-full ${inv.status === "paid" ? "bg-eh-sage/30 text-eh-sage-deep" : inv.status === "sent" ? "bg-eh-ochre/20 text-eh-ochre-deep" : "bg-eh-border-soft text-eh-muted"}`}>{t(`invoices.status.${inv.status}`, undefined, inv.status)}</span>
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-1">
              {inv.status === "paid" && inv.receipt_view_url && (
                <>
                  <a
                    href={inv.receipt_view_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full hover:bg-white text-eh-sage-deep"
                    title={t("invoices.actions.viewReceipt")}
                  >
                    <Printer size={14} />
                  </a>
                  <button
                    onClick={() => resendReceipt(inv)}
                    disabled={resending === inv.id}
                    className="p-2 rounded-full hover:bg-white text-eh-terracotta"
                    title={t("invoices.actions.resendReceipt")}
                  >
                    {resending === inv.id ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                  </button>
                </>
              )}
              {inv.payment_url && PAYABLE_LINK_STATUSES.has(inv.status) && (
                <>
                  <button onClick={() => { navigator.clipboard.writeText(inv.payment_url); }} className="p-2 rounded-full hover:bg-white" title={t("invoices.actions.copy_link")} data-testid={`invoice-copy-link-${inv.id}`}><Copy size={14} /></button>
                  <a href={inv.payment_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-white" title={tc("actions.edit")} data-testid={`invoice-open-link-${inv.id}`}><ExternalLink size={14} /></a>
                </>
              )}
              {/* Withheld for a row the server would refuse on currency (badge above says
                  why) — the disabled state is advisory only; the server's own refusal
                  (proved in test_catalogue_currency_disclosure.py) remains the authority. */}
              {inv.status === "draft" && !isLegacyCurrency(inv.currency, tenantCurrency) && (
                <button onClick={() => send(inv)} disabled={sending === inv.id} className="btn-primary text-xs" data-testid={`invoice-send-${inv.id}`}><Send size={12} /> {sending === inv.id ? tc("actions.loading") : t("invoices.actions.send")}</button>
              )}
              {CANCELLABLE_STATUSES.has(inv.status) && (
                <button
                  onClick={() => cancel(inv)}
                  className="p-2 rounded-full text-eh-muted hover:bg-white"
                  title={t("invoices.actions.cancel")}
                  aria-label={t("invoices.actions.cancel")}
                  data-testid={`invoice-cancel-${inv.id}`}
                ><Ban size={14} /></button>
              )}
              {/* Only an unnumbered draft can leave the books without a gap in the
                  sequence; every other invoice is cancelled instead (the server refuses
                  the rest with INVOICE_NOT_DELETABLE). */}
              {inv.status === "draft" && !inv.invoice_number && (
                <button onClick={() => remove(inv.id)} className="p-2 rounded-full text-red-600 hover:bg-red-50" data-testid={`invoice-delete-${inv.id}`}><Trash2 size={14} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


const Inp = ({ label, value, onChange, type = "text", min, step, placeholder, testid, error }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
    <input
      type={type}
      value={value}
      min={min}
      step={step}
      placeholder={placeholder}
      data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className={`mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm ${error ? invalidInputClass : ""}`}
    />
    <FieldError message={error} testId={testid ? `${testid}-error` : undefined} />
  </div>
);
